import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import Grid from '@mui/material/Grid';
import { useQueryClient } from '@tanstack/react-query';
import OrganisationIcon from 'components/Svgs/icons/Organisation';
import { Group, OrganisationResponse, useMoveLearnerOrganisation } from 'api';
import AccountOrganisationCard from 'components/OrganisationInfoCard/AccountOrganisationCard/AccountOrganisationCard';
import { useAuth } from 'contexts/AuthContext';
import Notification from 'components/Notification';
import LinkButton from 'components/LinkButton';
import { DetailsHeader } from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import ChangeOrgDialog from './ChangeOrgDialog';
import ConfirmOrgDialog from './ConfirmOrgDialog';

const changeOrgPermittedRoles = [
  'SUPER_ADMIN',
  'ADVANCED_USER',
  'GROUP_MANAGER',
];

const OrganisationInfo = ({
  name,
  currentOrg,
  dataRetentionPolicy,
  organisations,
  learnerId,
  parent,
}: {
  name: string;
  currentOrg: OrganisationResponse;
  dataRetentionPolicy?: { maximumInactivityPeriod: string };
  organisations: OrganisationResponse[];
  learnerId: string;
  parent?: Group;
}) => {
  const { t } = useTranslation();
  const [showChangeOrgDialog, setShowChangeOrgDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [newOrgId, setNewOrgId] = useState('');
  const [reason, setReason] = useState('');
  const { state } = useLocation();
  const locationState: any = state;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const isPermitted = user?.role && changeOrgPermittedRoles.includes(user.role);

  const { mutate, isError, isPending, reset } = useMoveLearnerOrganisation(
    currentOrg.id,
    queryClient,
  );

  const onChangeOrg = (orgId: string, reasonText: string) => {
    setNewOrgId(orgId);
    setReason(reasonText);
    setShowConfirmDialog(true);
  };

  const handleConfirmChangeOrg = () =>
    mutate(
      {
        learnerId,
        sourceOrganisationId: currentOrg.id,
        targetOrganisationId: newOrgId,
        reason,
      },
      {
        onSuccess: () => {
          setTimeout(() => {
            setShowConfirmDialog(false);
            navigate(`/learners/${newOrgId}/${learnerId}`, {
              state: {
                showTransferSuccess: true,
              },
            });
          }, 650);
        },
      },
    );

  return (
    <>
      <DetailsHeader
        Icon={OrganisationIcon}
        text={t('ui.event-management.learner.organisation')}
        fullWidth
        id="learner-organisation-info"
        sx={{ marginBottom: spacingSizeMap.S, marginLeft: '-0.5rem' }}
      />
      {locationState?.showTransferSuccess ? (
        <Notification
          message={t('ui.event-management.learner.transferred.notification')}
        />
      ) : null}
      <Grid
        container
        direction="row"
        sx={() => ({
          marginBottom: '1.5rem',
        })}
      >
        <AccountOrganisationCard
          id={currentOrg.id}
          name={name}
          dataRetentionPolicy={dataRetentionPolicy}
          type="Organisation"
          parent={parent}
        >
          {Boolean(isPermitted) && organisations.length >= 2 && (
            <LinkButton
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                padding: '0.5rem 0',
                lineHeight: '28px',
              }}
              onClick={() => setShowChangeOrgDialog(true)}
            >
              {t('ui.event-management.learners.organisation.change-org')}
            </LinkButton>
          )}
        </AccountOrganisationCard>
      </Grid>
      <ChangeOrgDialog
        open={showChangeOrgDialog}
        onClose={() => {
          setShowChangeOrgDialog(false);
        }}
        currentOrg={currentOrg}
        currentOrganisationCard={
          <AccountOrganisationCard
            sx={{ paddingTop: '4px' }}
            id={currentOrg.id}
            name={name}
            type="Organisation"
            dataRetentionPolicy={dataRetentionPolicy}
            parent={parent}
            variant="small"
          />
        }
        handleOrganisationChange={onChangeOrg}
      />
      <ConfirmOrgDialog
        open={showConfirmDialog}
        isLoading={isPending}
        isError={isError}
        onClose={() => {
          reset();
          setShowConfirmDialog(false);
        }}
        onConfirm={handleConfirmChangeOrg}
      />
    </>
  );
};
export default OrganisationInfo;
